import React from "react";

import PropTypes from "prop-types";

import { ATTACHMENT_TYPES } from "manager/config/constants";
import Settings from "shared/components/Settings";

import ApplicationSetupHeader from "../Header";
import { StyledSkeleton as Skeleton } from "../styled";
import { useAttachmentValidation } from "../DealSetupForm/AttachmentValidationContext";
import { ConsolidatedAttachmentGrid } from "../ConsolidatedAttachmentGrid";
import {
  buildApplicantTypes,
  buildItemDisplayLabel,
  getAttachmentsFilter,
  mapFinancialDocLabels,
  useAttachmentHandlers,
  useDocQuantitiesUiEnabled,
} from "../utils";
import RequiredAttachmentsLegacy from "./RequiredAttachmentsLegacy";

const APPLICANT_TYPES_CONFIG = buildApplicantTypes({
  applicant: "requiresApplicantAttachements",
  occupant: "requiresOccupantAttachements",
  guarantor: "requiresGuarantorAttachements",
});

const ATTACHMENT_TYPE = ATTACHMENT_TYPES.APPLICATION;
const getRequiredAttachments = getAttachmentsFilter(ATTACHMENT_TYPE);

const RequiredAttachments = ({ loading, disabled, forSettingsLabel }) => {
  const uiEnabled = useDocQuantitiesUiEnabled();
  if (!uiEnabled) {
    return (
      <RequiredAttachmentsLegacy
        loading={loading}
        disabled={disabled}
        forSettingsLabel={forSettingsLabel}
      />
    );
  }

  return <RequiredAttachmentsGrid loading={loading} disabled={disabled} />;
};

const RequiredAttachmentsGrid = ({ loading, disabled }) => {
  const { reportWarnings } = useAttachmentValidation();
  const {
    values,
    setFieldValue,
    onChangeAttachment,
    onChangeQuantity,
    handleAddCustomDoc,
    handleDeleteDoc,
  } = useAttachmentHandlers({
    attachmentType: ATTACHMENT_TYPE,
    personas: APPLICANT_TYPES_CONFIG,
  });

  const allItems = APPLICANT_TYPES_CONFIG.flatMap((at) =>
    mapFinancialDocLabels(getRequiredAttachments(values, at.applicantType))
  );

  return (
    <div id="required-attachments-grid">
    <Settings
      header={
        <Skeleton loading={loading}>
          <ApplicationSetupHeader
            title="Required Attachments"
            subTitle="Please manage the attachments that you want to require from applicants, occupants and guarantors"
          />
        </Skeleton>
      }
      nodes={[
        {
          key: 1,
          component: (
            <Skeleton loading={loading}>
              <ConsolidatedAttachmentGrid
                personas={APPLICANT_TYPES_CONFIG}
                items={allItems}
                values={values}
                onTogglePersona={(field, value) => setFieldValue(field, value)}
                onToggleItem={onChangeAttachment}
                onChangeQuantity={onChangeQuantity}
                onAddCustomDoc={handleAddCustomDoc}
                onDeleteDoc={handleDeleteDoc}
                buildLabel={buildItemDisplayLabel}
                sectionTitle="Required Attachments"
                sectionId="required-attachments-grid"
                onValidationChange={(w) =>
                  reportWarnings("requiredAttachments", w)
                }
                disabled={disabled}
              />
            </Skeleton>
          ),
        },
      ]}
    />
    </div>
  );
};

RequiredAttachments.propTypes = {
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  forSettingsLabel: PropTypes.string,
};

RequiredAttachments.defaultProps = {
  loading: false,
  disabled: false,
  forSettingsLabel: "Settings",
};

export default RequiredAttachments;
